export interface AudioDownloadProgress {
  loadedBytes: number
  totalBytes: number | null
  percent: number | null
}

const MIN_STORAGE_RESERVE_BYTES = 16 * 1024 * 1024

export function audioCacheKey(mediaFileId: string | null | undefined, fallback: string): string {
  const stableId = mediaFileId?.trim() || fallback.trim()
  return `media:${stableId}`
}

function parseContentLength(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

async function ensureStorageCapacity(totalBytes: number | null): Promise<void> {
  if (!totalBytes || typeof navigator === 'undefined' || !navigator.storage?.estimate) return

  const estimate = await navigator.storage.estimate()
  const quota = estimate.quota
  const usage = estimate.usage ?? 0
  if (!quota) return

  const reserve = Math.min(MIN_STORAGE_RESERVE_BYTES, Math.ceil(totalBytes * 0.05))
  if (quota - usage < totalBytes + reserve) {
    throw new Error('기기 저장 공간이 부족하여 음원 전체를 내려받을 수 없습니다.')
  }

  // 브라우저가 허용하는 경우 캐시가 자동 정리 대상이 되지 않도록 영구 저장을 요청합니다.
  try {
    await navigator.storage.persist?.()
  } catch {
    // 영구 저장 요청이 거부돼도 일반 IndexedDB 저장은 계속 시도합니다.
  }
}

export async function downloadAudioFully(
  url: string,
  onProgress?: (progress: AudioDownloadProgress) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    signal,
  })

  if (!response.ok) {
    throw new Error(`음원 다운로드 응답 ${response.status}`)
  }

  const totalBytes = parseContentLength(response.headers.get('Content-Length'))
  await ensureStorageCapacity(totalBytes)

  const contentType = response.headers.get('Content-Type') || 'application/octet-stream'
  if (!response.body) {
    const blob = await response.blob()
    onProgress?.({ loadedBytes: blob.size, totalBytes: totalBytes ?? blob.size, percent: 100 })
    return blob
  }

  const reader = response.body.getReader()
  const chunks: BlobPart[] = []
  let loadedBytes = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    if (!value || value.byteLength === 0) continue

    chunks.push(value.slice().buffer)
    loadedBytes += value.byteLength
    onProgress?.({
      loadedBytes,
      totalBytes,
      percent: totalBytes == null ? null : Math.min(100, Math.round(loadedBytes / totalBytes * 100)),
    })
  }

  const blob = new Blob(chunks, { type: contentType })
  if (blob.size <= 0) throw new Error('내려받은 음원이 비어 있습니다.')

  onProgress?.({ loadedBytes: blob.size, totalBytes: totalBytes ?? blob.size, percent: 100 })
  return blob
}
